<template>
  <div class="balance-page">
    <div class="page-header">
      <h2 class="page-title">平衡校验</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">空间节点</span>
        <el-cascader
          v-model="spacePath"
          :options="spaceCascader"
          :props="{ checkStrictly: true, emitPath: false }"
          clearable
          size="small"
          placeholder="全部节点"
          style="width: 220px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">能源类型</span>
        <el-select v-model="energyType" size="small" style="width: 120px">
          <el-option
            v-for="item in energyTypes"
            :key="item.key"
            :label="item.label"
            :value="item.key"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">时间周期</span>
        <el-radio-group v-model="period" size="small">
          <el-radio-button label="day">日</el-radio-button>
          <el-radio-button label="month">月</el-radio-button>
          <el-radio-button label="year">年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-item">
        <span class="filter-label">时间段</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          value-format="yyyy-MM-dd"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">不平衡阈值</span>
        <el-input-number
          v-model="threshold"
          :min="1"
          :max="50"
          size="small"
          controls-position="right"
        />
        <span class="unit-suffix">%</span>
      </div>
      <div class="filter-item" style="margin-left: auto">
        <el-button type="primary" size="small" @click="loadData">查询</el-button>
        <el-button size="small" @click="rulesDialogVisible = true">校验规则</el-button>
      </div>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="校验节点数"
        :today="summary.checkedNodes"
        :annual="summary.checkedNodes"
        unit="个"
        annual-unit="个"
        icon="el-icon-s-data"
        color="#409EFF"
      />
      <stat-card
        title="异常节点数"
        :today="summary.abnormalNodes"
        :annual="summary.abnormalNodes"
        unit="个"
        annual-unit="个"
        icon="el-icon-warning-outline"
        color="#F56C6C"
      />
      <stat-card
        title="平均不平衡率"
        :today="summary.avgImbalanceRate"
        :annual="summary.avgImbalanceRate"
        unit="%"
        annual-unit="%"
        icon="el-icon-data-line"
        color="#E6A23C"
      />
    </div>

    <chart-card title="不平衡率趋势" class="chart-section">
      <base-chart :option="chartOption" />
    </chart-card>

    <div class="table-section">
      <el-table :data="tableList" border stripe size="small">
        <el-table-column prop="spacePath" label="空间节点" min-width="260" show-overflow-tooltip />
        <el-table-column prop="levelRelation" label="层级关系" width="140" />
        <el-table-column prop="parentInput" label="上级输入 (kWh)" width="130" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.parentInput, 0) }}</template>
        </el-table-column>
        <el-table-column prop="childSum" label="下级合计 (kWh)" width="130" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.childSum, 0) }}</template>
        </el-table-column>
        <el-table-column prop="difference" label="平衡差值 (kWh)" width="130" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.difference, 0) }}</template>
        </el-table-column>
        <el-table-column prop="imbalanceRate" label="不平衡率 (%)" width="120" align="right">
          <template slot-scope="{ row }">
            <span :class="{ 'text-danger': row.imbalanceRate > threshold }">
              {{ row.imbalanceRate }}%
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="statusLabel" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)" size="mini">{{ row.statusLabel }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="goToAnalysis(row)">查看能流</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog title="校验规则" :visible.sync="rulesDialogVisible" width="420px">
      <el-form label-width="140px" size="small">
        <el-form-item label="不平衡率预警阈值">
          <el-input-number
            v-model="rulesForm.threshold"
            :min="1"
            :max="50"
            controls-position="right"
          />
          <span class="unit-suffix">%</span>
        </el-form-item>
        <el-form-item label="包含损耗节点">
          <el-switch v-model="rulesForm.includeLoss" />
        </el-form-item>
        <el-form-item label="校验层级深度">
          <el-input-number
            v-model="rulesForm.maxDepth"
            :min="1"
            :max="5"
            controls-position="right"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="rulesDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveRules">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import StatCard from '@/components/common/StatCard.vue'
import { getBalanceData, getBalanceRules, saveBalanceRules } from '@/api/modules/energyFlow'
import { formatNumber } from '@/utils/format'

export default {
  name: 'EnergyFlowBalance',
  components: { FilterBar, ChartCard, BaseChart, StatCard },
  data() {
    return {
      spacePath: '',
      spaceCascader: [],
      energyTypes: [],
      energyType: 'electricity',
      period: 'day',
      dateRange: ['2025-07-01', '2025-07-07'],
      threshold: 10,
      summary: {
        checkedNodes: 0,
        abnormalNodes: 0,
        avgImbalanceRate: 0
      },
      tableList: [],
      chartOption: {},
      rulesDialogVisible: false,
      rulesForm: {
        threshold: 10,
        includeLoss: true,
        maxDepth: 5
      }
    }
  },
  created() {
    this.applyRouteQuery()
    this.loadRules()
    this.loadData()
  },
  methods: {
    formatNumber,
    applyRouteQuery() {
      const { energyType, period, startDate, endDate, spaceId, threshold } = this.$route.query
      if (energyType) this.energyType = energyType
      if (period) this.period = period
      if (startDate && endDate) this.dateRange = [startDate, endDate]
      if (spaceId) this.spacePath = spaceId
      if (threshold) this.threshold = Number(threshold)
    },
    buildQueryParams() {
      return {
        spaceId: this.spacePath || undefined,
        energyType: this.energyType,
        period: this.period,
        startDate: this.dateRange && this.dateRange[0],
        endDate: this.dateRange && this.dateRange[1],
        threshold: this.threshold
      }
    },
    async loadRules() {
      const res = await getBalanceRules()
      this.rulesForm = { ...this.rulesForm, ...res.data }
      if (!this.$route.query.threshold) {
        this.threshold = res.data.threshold
      }
    },
    async loadData() {
      const params = this.buildQueryParams()
      const res = await getBalanceData(params)
      const data = res.data
      this.spaceCascader = data.spaceCascader
      this.energyTypes = data.energyTypes
      this.summary = data.summary
      this.tableList = data.list
      this.rulesForm = { ...this.rulesForm, ...data.rules }
      this.buildChartOption(data.trend)
    },
    buildChartOption(trend) {
      this.chartOption = {
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const item = params[0]
            return `${item.name}<br/>不平衡率: ${item.value}%`
          }
        },
        grid: { left: 50, right: 30, top: 30, bottom: 30 },
        xAxis: {
          type: 'category',
          data: trend.map(item => item.date),
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          name: '%',
          axisLabel: { formatter: '{value}%' }
        },
        series: [
          {
            name: '不平衡率',
            type: 'line',
            data: trend.map(item => item.rate),
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#409EFF', width: 2 },
            itemStyle: { color: '#409EFF' },
            areaStyle: { color: 'rgba(64, 158, 255, 0.12)' },
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: { color: '#F56C6C', type: 'dashed' },
              data: [{ yAxis: this.threshold, label: { formatter: `阈值 ${this.threshold}%` } }]
            }
          }
        ]
      }
    },
    statusTagType(status) {
      const map = { normal: 'success', warning: 'warning', danger: 'danger' }
      return map[status] || 'info'
    },
    async saveRules() {
      await saveBalanceRules(this.rulesForm)
      this.threshold = this.rulesForm.threshold
      this.rulesDialogVisible = false
      this.$message.success('校验规则已保存')
      this.loadData()
    },
    goToAnalysis(row) {
      this.$router.push({
        path: '/energy-flow/analysis',
        query: {
          energyType: this.energyType,
          period: this.period,
          startDate: this.dateRange && this.dateRange[0],
          endDate: this.dateRange && this.dateRange[1],
          spaceIds: row.spaceId
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.stat-cards-3 {
  grid-template-columns: repeat(3, 1fr);
}

.unit-suffix {
  margin-left: 4px;
  color: @text-secondary;
  font-size: 13px;
}

.chart-section {
  height: 360px;
  margin-bottom: 16px;
}

.table-section {
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 16px;
  overflow-x: auto;
}
</style>
